import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as RKeyboardEvent,
  type MutableRefObject,
  type PointerEvent as RPointerEvent,
  type Ref,
} from "react"

// Shared resizable-split primitive for the aaa-2 console.
//
// Generalizes the three hand-rolled pointer-drag resizers (ThreadPanel's thread
// column, Shell's left/right rails, SplitView's messaging list) into ONE hook +
// handle. Mirrors their behavior exactly:
//   • pointer-capture drag (onPointerDown/Move/Up), clamp to [min, max]
//   • `edge` controls anchor + drag direction:
//       "right" → handle on the element's RIGHT edge; drag RIGHT grows it
//                 (left-anchored list — SplitView, Shell left rail).
//       "left"  → handle on the element's LEFT edge; drag LEFT grows it
//                 (right-anchored thread column / right rail — ThreadPanel,
//                 Shell right rail).
//   • optional localStorage persistence under `storageKey`; SSR-safe — never
//     reads localStorage during render, hydrates in a mount effect.
//   • `document.body.classList` cursor (`col-resizing`) while dragging, matching
//     Shell — gives a global col-resize cursor + suppresses text selection.
//   • keyboard a11y: ArrowLeft/Right on the handle nudge the width by `step`,
//     clamped (direction respects `edge` so the key matches the visual grow).
//
// Framework-light, no deps. Nothing in the app imports this yet (wave 1 builds
// it; later waves adopt it).

export type ResizeEdge = "left" | "right"

export type UseResizableWidthOptions = {
  /** localStorage key for persistence. Omit for ephemeral (non-persisted) width. */
  storageKey?: string
  /** Seed width (px) used on first render + as the SSR width, until a persisted value hydrates. */
  defaultWidth: number
  /** Lower clamp bound (px). */
  min: number
  /** Upper clamp bound (px). */
  max: number
  /**
   * Which edge the handle straddles, and therefore the drag→grow direction.
   * "right" (default) = left-anchored element, drag right widens.
   * "left" = right-anchored element, drag left widens.
   */
  edge?: ResizeEdge
  /** Keyboard nudge step (px) for ArrowLeft/Right. Default 16. */
  step?: number
}

/** Pointer + keyboard handlers and presentation props to spread onto a `<ResizeHandle />` (or a bare div). */
export type ResizeHandleProps = {
  onPointerDown: (e: RPointerEvent<HTMLDivElement>) => void
  onPointerMove: (e: RPointerEvent<HTMLDivElement>) => void
  onPointerUp: (e: RPointerEvent<HTMLDivElement>) => void
  onKeyDown: (e: RKeyboardEvent<HTMLDivElement>) => void
  /** Which edge the handle sits on — drives ResizeHandle's positioning class. */
  edge: ResizeEdge
  /** True mid-drag — lets the consumer reflect active state (e.g. force the hover line on). */
  "data-resize-handle-active"?: true
  role: "separator"
  "aria-orientation": "vertical"
  tabIndex: 0
  title: string
  style: CSSProperties
}

export type UseResizableWidthResult = {
  /** Current clamped width in px. Apply to the resizable element's `style.width`. */
  width: number
  /** True while a pointer drag is in progress. */
  dragging: boolean
  /** Spread onto `<ResizeHandle />` (or a div) to wire up the drag + a11y. */
  handleProps: ResizeHandleProps
}

const clamp = (w: number, min: number, max: number, fallback: number) =>
  Math.max(min, Math.min(max, Math.round(Number.isFinite(w) ? w : fallback)))

/**
 * Drag-to-resize width state for a panel column. Returns the current `width`,
 * a `dragging` flag, and `handleProps` to spread onto a `<ResizeHandle />`.
 *
 * @example
 * const { width, handleProps } = useResizableWidth({
 *   storageKey: "aaa2.threadColWidth",
 *   defaultWidth: 380, min: 300, max: 600, edge: "left",
 * })
 * return (
 *   <aside style={{ width }} className="relative …">
 *     <ResizeHandle {...handleProps} />
 *     …
 *   </aside>
 * )
 */
export function useResizableWidth({
  storageKey,
  defaultWidth,
  min,
  max,
  edge = "right",
  step = 16,
}: UseResizableWidthOptions): UseResizableWidthResult {
  const [width, setWidthState] = useState(() => clamp(defaultWidth, min, max, defaultWidth))
  const [dragging, setDragging] = useState(false)
  const widthRef = useRef(width)

  const setWidth = useCallback(
    (w: number) => {
      const c = clamp(w, min, max, defaultWidth)
      widthRef.current = c
      setWidthState(c)
      return c
    },
    [min, max, defaultWidth],
  )

  // SSR-safe: never read localStorage during render — hydrate on mount.
  useEffect(() => {
    if (!storageKey) return
    try {
      const v = localStorage.getItem(storageKey)
      if (v != null) setWidth(Number(v))
    } catch {
      /* ignore */
    }
  }, [storageKey, setWidth])

  // Re-clamp when the bounds change. Callers pass a CONTAINER-AWARE `max`
  // (e.g. `containerWidth - siblingMinPx`) that shrinks as the canvas narrows;
  // when it drops below the current width we pull the width back into the new
  // band — in real pixels — so the flex-fill sibling never loses its pixel
  // floor (and the persisted width can't strand the layout overflowing).
  useEffect(() => {
    setWidth(widthRef.current)
  }, [min, max, setWidth])

  const persist = useCallback(() => {
    if (!storageKey) return
    try {
      localStorage.setItem(storageKey, String(widthRef.current))
    } catch {
      /* ignore */
    }
  }, [storageKey])

  const dragRef = useRef<{ startX: number; startW: number } | null>(null)

  const onPointerDown = useCallback((e: RPointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startW: widthRef.current }
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
    try {
      document.body.classList.add("col-resizing")
    } catch {
      /* ignore */
    }
  }, [])

  const onPointerMove = useCallback(
    (e: RPointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return
      const dx = e.clientX - dragRef.current.startX
      // "right" edge (left-anchored) → drag right = wider.
      // "left" edge (right-anchored) → drag left = wider (invert dx).
      const delta = edge === "left" ? -dx : dx
      setWidth(dragRef.current.startW + delta)
    },
    [edge, setWidth],
  )

  const endDrag = useCallback(
    (e: RPointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return
      dragRef.current = null
      setDragging(false)
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      try {
        document.body.classList.remove("col-resizing")
      } catch {
        /* ignore */
      }
      persist()
    },
    [persist],
  )

  const onKeyDown = useCallback(
    (e: RKeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
      e.preventDefault()
      // Match the visual grow direction: on a "right" edge ArrowRight widens; on
      // a "left" edge ArrowLeft widens.
      const towardWider = edge === "left" ? e.key === "ArrowLeft" : e.key === "ArrowRight"
      const next = setWidth(widthRef.current + (towardWider ? step : -step))
      // Persist keyboard nudges too (no pointerup to hook).
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, String(next))
        } catch {
          /* ignore */
        }
      }
    },
    [edge, step, setWidth, storageKey],
  )

  const handleProps: ResizeHandleProps = {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onKeyDown,
    edge,
    ...(dragging ? { "data-resize-handle-active": true as const } : {}),
    role: "separator",
    "aria-orientation": "vertical",
    tabIndex: 0,
    title: "Drag to resize",
    style: { touchAction: "none" },
  }

  return { width, dragging, handleProps }
}

// ── ResizeHandle ────────────────────────────────────────────────────────────

export type ResizeHandleProps2 = ResizeHandleProps & {
  /** Extra classes appended after the built-in handle classes. */
  className?: string
  /** Accessible label, e.g. "Resize sidebar". */
  "aria-label"?: string
}

/**
 * The visible splitter handle. A thin hit-area straddling the panel edge
 * (3px wide, centered on the border via a -1.5 offset matching the existing
 * resizers), with `cursor-col-resize`, `touch-action: none`, and a plum hover
 * line that mirrors styles.css `.splitter` + ThreadPanel's
 * `group-hover/resize:bg-plum-300`. Dark parity included.
 *
 * Spread the hook's `handleProps`; `edge` (carried in handleProps) picks the
 * left/right offset so it lands on the correct border.
 *
 * @example
 * <ResizeHandle {...handleProps} aria-label="Resize sidebar" />
 */
// ── Container-aware sizing helpers ───────────────────────────────────────────

/**
 * Compose multiple refs (callback or object) onto one node. Lets a split
 * container carry BOTH the reading-pane observer ref AND the width-measuring ref
 * (`useElementWidth`) without an extra wrapper element.
 */
export function mergeRefs<T>(
  ...refs: Array<Ref<T> | undefined | null>
): (node: T | null) => void {
  return (node: T | null) => {
    for (const r of refs) {
      if (typeof r === "function") r(node)
      else if (r) (r as MutableRefObject<T | null>).current = node
    }
  }
}

/**
 * Measure an element's content-box width via ResizeObserver. Returns the live
 * `width` (px; 0 until the first measure → callers fall back to a static cap)
 * and a callback ref to attach to the element.
 *
 * Pair with `useResizableWidth` to derive a container-aware `max` so a pane can
 * only grow until its sibling keeps a real-pixel minimum:
 *
 *   const [w, measureRef] = useElementWidth()
 *   const max = w > 0 ? Math.max(MIN, Math.min(HARD_CAP, w - SIBLING_MIN)) : HARD_CAP
 */
export function useElementWidth(): readonly [number, (el: HTMLElement | null) => void] {
  const [width, setWidth] = useState(0)
  const roRef = useRef<ResizeObserver | null>(null)
  const ref = useCallback((el: HTMLElement | null) => {
    roRef.current?.disconnect()
    roRef.current = null
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    roRef.current = ro
  }, [])
  return [width, ref] as const
}

export function ResizeHandle({
  edge,
  className,
  "data-resize-handle-active": active,
  "aria-label": ariaLabel,
  ...rest
}: ResizeHandleProps2) {
  const sideClass = edge === "left" ? "-left-1.5" : "-right-1.5"
  return (
    <div
      {...rest}
      data-resize-handle-active={active}
      aria-label={ariaLabel}
      className={
        "group/resize absolute top-0 z-30 h-full w-3 cursor-col-resize " +
        sideClass +
        (className ? " " + className : "")
      }
    >
      <div
        className={
          "mx-auto h-full w-px transition group-hover/resize:bg-plum-300 dark:group-hover/resize:bg-white/20 " +
          (active ? "bg-plum-300 dark:bg-white/20" : "bg-transparent")
        }
      />
    </div>
  )
}
